import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { EyeOff } from 'lucide-react';

const NETWORKS = [
  { ticker: 'XMR', name: 'MONERO', note: 'Privacy by default.', detail: "Transaction details are obscured for every participant, by design." },
  { ticker: 'ZEC', name: 'ZCASH', note: 'Privacy with selective disclosure.', detail: "Transactions can be shielded, with disclosure available at the holder's discretion." },
];

export function PrivateMoneySection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="private-money" className="py-32 px-6 relative overflow-hidden" data-testid="private-money-section" ref={ref}>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FFC700]/20 to-transparent" />

      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-12"
        >
          <span className="mono-tag text-[#FFC700]">// SECTION :: PRIVATE_MONEY</span>
          <h2 className="section-title mt-4">PRIVATE<br /><span className="text-[#FFC700]">MONEY</span></h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="space-y-4 mb-12"
        >
          <p className="body-text text-white/80 leading-relaxed" data-testid="private-money-intro">
            Every financial transaction can reveal information — where you were, what you bought, who you paid, when you paid.
          </p>
          <p className="body-text text-white/60 leading-relaxed">
            Financial privacy is therefore also informational privacy. ZATO is interested in systems that allow individuals to retain greater control over that information.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="grid sm:grid-cols-2 gap-4 mb-8"
          data-testid="private-money-networks"
        >
          {NETWORKS.map((n) => (
            <div key={n.ticker} className="terminal-panel" data-testid={`private-money-${n.ticker.toLowerCase()}`}>
              <div className="terminal-panel-header">
                <span className="terminal-panel-dot" />
                {n.name} :: {n.ticker}
              </div>
              <div className="p-4">
                <p className="font-mono text-xs text-[#FFC700] mb-2">{n.note}</p>
                <p className="body-text text-white/50 text-sm leading-relaxed">{n.detail}</p>
              </div>
            </div>
          ))}
        </motion.div>

        <p className="font-mono text-[10px] text-white/20 flex items-center gap-2" data-testid="private-money-disclaimer">
          <EyeOff size={11} />
          NOT FINANCIAL ADVICE — NO PRICE DATA — RESEARCH FOR YOURSELF
        </p>
      </div>
    </section>
  );
}
